import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "hooks/useFetch";
import { RoutePaths } from "enums/RoutePaths";
import { Button, Card, EmptyState, Icon, PageHeader } from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import {
  AdminRequestPage,
  adminRequestsQuery,
  ContactPage,
  contactQuery,
  firstLine,
  formatOslo,
  InquirySummary,
  MosqueSuggestionPage,
  requestInquiryRefresh,
  senderText,
  setInquirySummary,
  suggestionsQuery,
  SUMMARY_URL,
} from "./inquiries";
import styles from "./Inquiries.module.scss";

/*
 * Superadmin: oversikt over Henvendelser – antall nye per type, og de siste nye i hver.
 */

const RECENT = 4;

const nf = (n: number | undefined) =>
  n == null ? "–" : n.toLocaleString("nb-NO");

const InquiriesOverviewPage: React.FC = () => {
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const navigate = useNavigate();

  const [summary, setSummary] = useState<InquirySummary | null>(null);
  const [contact, setContact] = useState<ContactPage | null>(null);
  const [requests, setRequests] = useState<AdminRequestPage | null>(null);
  const [suggestions, setSuggestions] = useState<MosqueSuggestionPage | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    const run = executeRef.current;
    const [s, c, r, m] = await Promise.all([
      run(SUMMARY_URL, "GET"),
      run(
        contactQuery({
          status: "NEW",
          category: "",
          q: "",
          page: 0,
          size: RECENT,
        }),
        "GET",
      ),
      run(
        adminRequestsQuery({ status: "PENDING", q: "", page: 0, size: RECENT }),
        "GET",
      ),
      run(
        suggestionsQuery({ status: "NEW", q: "", page: 0, size: RECENT }),
        "GET",
      ),
    ]);
    if (s) {
      setSummary(s);
      setInquirySummary(s);
    }
    setContact(c);
    setRequests(r);
    setSuggestions(m);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stat = (
    count: number | undefined,
    label: string,
    hint: string,
    path: RoutePaths,
  ) => (
    <button
      type="button"
      className={`${styles.stat} ${count ? styles.statOn : ""}`}
      onClick={() => navigate(path)}
    >
      <b>{nf(count)}</b>
      <span>{label}</span>
      <small>{hint}</small>
    </button>
  );

  return (
    <>
      <PageHeader
        title="Henvendelser"
        subtitle="Kontakt oss-meldinger, søknader om moské-admin og forslag om nye moskeer."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={() => {
              load();
              requestInquiryRefresh();
            }}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.stats}>
        {stat(
          summary?.contactUnread,
          "Uleste meldinger",
          "Kontakt oss fra appene og ibønn.no",
          RoutePaths.ContactInbox,
        )}
        {stat(
          summary?.adminRequestsPending,
          "Admin-søknader som venter",
          "Godkjenn eller avslå",
          RoutePaths.AdminRequests,
        )}
        {stat(
          summary?.mosqueSuggestionsNew,
          "Nye moskéforslag",
          "Moskeer som mangler i iBønn",
          RoutePaths.MosqueSuggestions,
        )}
      </div>

      <div className={styles.recent}>
        <Card title="Siste uleste meldinger">
          {contact?.items.length ? (
            <ul className={styles.recentList}>
              {contact.items.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => navigate(RoutePaths.ContactInbox)}
                  >
                    <b className={styles.clamp}>{firstLine(m.message, 90)}</b>
                    <span className={styles.muted}>
                      {senderText(m)} · {formatOslo(m.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="inbox" title="Ingen uleste">
              Alle meldinger er lest.
            </EmptyState>
          )}
        </Card>
        <Card title="Søknader som venter">
          {requests?.items.length ? (
            <ul className={styles.recentList}>
              {requests.items.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(RoutePaths.AdminRequests)}
                  >
                    <b className={styles.clamp}>
                      {r.fullName} → {r.mosqueName || r.mosqueOrgNr}
                    </b>
                    <span className={styles.muted}>
                      {r.role} · {formatOslo(r.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="users" title="Ingen venter">
              Ingen søknader venter på svar.
            </EmptyState>
          )}
        </Card>
        <Card title="Nye moskéforslag">
          {suggestions?.items.length ? (
            <ul className={styles.recentList}>
              {suggestions.items.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => navigate(RoutePaths.MosqueSuggestions)}
                  >
                    <b className={styles.clamp}>
                      {s.name}, {s.city}
                    </b>
                    <span className={styles.muted}>
                      {s.isAffiliated ? "Fra moskeen · " : ""}
                      {formatOslo(s.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="mosque" title="Ingen nye">
              Ingen nye forslag.
            </EmptyState>
          )}
        </Card>
      </div>
    </>
  );
};

export default InquiriesOverviewPage;
